import { useState, useEffect } from 'react';
import { Chrome, Zap, Check, ExternalLink } from 'lucide-react';

/**
 * "Get the extension" landing page. Reworked 2026-07 -- the previous version
 * described a copy-a-token / paste-into-the-extension flow that the current
 * extension (extension/, not the old superseded chrome-extension/) has never
 * supported: its popup only has Connect/Disconnect buttons, no paste field.
 * The real flow (already correctly wired in extension/popup.js) is:
 *   click the toolbar icon -> Connect -> opens /extension-connect, which
 *   auto-mints a token and hands it to the extension via postMessage.
 * This page's job is just: tell the user how to install it, and reflect
 * whether it's actually detected (the qudetv.js content script sets
 * localStorage 'qude_ext_installed' the moment it's active on this domain).
 */
export default function ExtensionToken() {
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const check = () => setInstalled(localStorage.getItem('qude_ext_installed') === '1');
    check();
    // The content script sets this on load -- poll briefly in case this page
    // was open before the extension was installed/reloaded.
    const t1 = setTimeout(check, 500);
    const t2 = setTimeout(check, 1500);
    const t3 = setTimeout(check, 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div className="max-w-xl mx-auto space-y-6 pb-10 pt-2">

      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>
          Get the Extension
        </h1>
        <p className="text-neutral-500 mt-1 text-sm">Auto-track what you watch in Chrome</p>
      </div>

      {/* Live status */}
      <div className="flex items-center gap-3 p-4 rounded-2xl border" style={{
        background: installed ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)',
        borderColor: installed ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.08)',
      }}>
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: installed ? '#22c55e' : '#555' }} />
        <p className="text-sm font-semibold text-white">
          {installed ? 'Extension detected on this page' : 'Extension not detected yet'}
        </p>
      </div>

      {/* Steps */}
      <div className="space-y-3">

        {/* Step 1 */}
        <div className="flex gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-white/5">
          <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-sm font-black text-violet-400">1</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white mb-1">Install the extension</p>
            <p className="text-xs text-neutral-500 leading-relaxed mb-3">
              Get it from the Chrome Web Store. (Loading it manually in developer mode? Use the <code className="text-violet-400 bg-violet-500/10 px-1 py-0.5 rounded">extension/</code> folder — not <code className="text-neutral-500 bg-white/5 px-1 py-0.5 rounded line-through">chrome-extension/</code>, which is retired.)
            </p>
            <a
              href="https://chromewebstore.google.com/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}
            >
              <Chrome className="w-3.5 h-3.5" /> Open Chrome Web Store <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-white/5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ background: installed ? 'rgba(16,185,129,0.2)' : 'rgba(124,58,237,0.2)' }}>
            {installed
              ? <Check className="w-4 h-4 text-emerald-400" />
              : <span className="text-sm font-black text-violet-400">2</span>}
          </div>
          <div>
            <p className="text-sm font-bold text-white mb-1">Connect it to your account</p>
            <p className="text-xs text-neutral-500 leading-relaxed">
              Click the Qude icon in your Chrome toolbar, then click <strong className="text-white">Connect to Qude</strong>. That's it — no code to copy or paste, it links automatically.
            </p>
          </div>
        </div>

      </div>

      {/* What happens next */}
      <div className="p-5 rounded-2xl border border-white/5" style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #0a1a2e 100%)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-violet-400" />
          <span className="text-xs font-black tracking-widest text-neutral-400 uppercase">What happens next</span>
        </div>
        <ul className="space-y-2 text-xs text-neutral-400 leading-relaxed">
          <li className="flex items-start gap-2"><span className="text-violet-400 mt-0.5">✦</span> The extension runs silently in the background</li>
          <li className="flex items-start gap-2"><span className="text-violet-400 mt-0.5">✦</span> When you watch Netflix, Hulu, Disney+ or any connected service, it detects the show after 2 minutes of real content</li>
          <li className="flex items-start gap-2"><span className="text-violet-400 mt-0.5">✦</span> The show is automatically added or updated in your Qude watchlist</li>
          <li className="flex items-start gap-2"><span className="text-violet-400 mt-0.5">✦</span> Ads and short clips are ignored — only real shows get logged</li>
        </ul>
      </div>
    </div>
  );
}
